/**
 * Client API typé.
 *
 * Authentification par cookie de session httpOnly : toutes les requêtes incluent les cookies
 * (`credentials: "include"`) et aucun jeton n'est stocké côté JavaScript (jamais exposé au bundle).
 * Une réponse 401 lève `ApiError` avec `status = 401`, que la couche d'authentification interprète
 * comme « session expirée » pour revenir à l'écran de connexion.
 */

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export interface Me {
  id: string;
  email: string;
  displayName: string | null;
  companyId: string | null;
}

type Json = Record<string, unknown>;

/** Interprète une réponse fetch : 204 → undefined, JSON sinon, erreur typée si non-2xx. */
async function handle<T>(res: Response): Promise<T> {
  if (res.status === 204) return undefined as T;
  let payload: unknown = null;
  const text = await res.text();
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = text;
    }
  }
  if (!res.ok) {
    const message =
      payload && typeof payload === "object" && "error" in payload
        ? String((payload as { error: unknown }).error)
        : `Erreur ${res.status}.`;
    throw new ApiError(res.status, message);
  }
  return payload as T;
}

async function request<T>(method: string, path: string, body?: Json): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      method,
      credentials: "include",
      headers: body ? { "content-type": "application/json" } : undefined,
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new ApiError(0, "Réseau indisponible.");
  }
  return handle<T>(res);
}

/** Envoi multipart (segments audio, photos) : le navigateur fixe lui-même le Content-Type. */
async function requestForm<T>(path: string, form: FormData): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, { method: "POST", credentials: "include", body: form });
  } catch {
    throw new ApiError(0, "Réseau indisponible.");
  }
  return handle<T>(res);
}

export type CaptureMode = "daily" | "standalone";

export interface StartCaptureResult {
  captureId: string;
  standaloneNoteId?: string;
}

export interface Preferences {
  analyzePhotos: boolean;
  finalizeDelaySeconds: number;
  autoSummaryDay: boolean;
  autoSummaryWeekPartial: boolean;
  autoSummaryWeek: boolean;
  replaceSummary: boolean;
  autoDeleteIntermediateSummaries: boolean;
  dailyReportEnabled: boolean;
  dailyReportEmail: string | null;
  dailyReportTime: string | null;
  dailyReportIncludeWeek: boolean;
  weeklyReportEnabled: boolean;
  weeklyReportEmail: string | null;
  weeklyReportTime: string | null;
  dailyTranscriptionEnabled: boolean;
  dailyTranscriptionEmail: string | null;
  dailyTranscriptionTime: string | null;
  promptSummaryDay: string;
  promptSummaryWeekPartial: string;
  promptSummaryWeek: string;
  promptSummaryStandalone: string;
  showTimestamps: boolean;
}

export interface SettingsLimits {
  quotaMinutes: number;
  quotaAudioBytes: number;
  audioRetentionDays: number;
  trashRetentionDays: number;
  subscriptionTier: string | null;
}

export interface SettingsResponse {
  limits: SettingsLimits | null;
  preferences: Preferences | null;
}

export interface UsageData {
  current: {
    year: number;
    month: number;
    transcribedMinutes: number;
    transcribedSeconds: number;
    quotaMinutes: number;
    summariesCount: number;
    audioBytes: number;
    quotaAudioBytes: number;
  };
  previousMonths: { year: number; month: number; transcribedMinutes: number; summariesCount: number }[];
}

export interface TrashData {
  captures: { id: string; mode: string; kind: string; deletedAt: string }[];
  standaloneNotes: { id: string; title: string | null; deletedAt: string }[];
  summaries: { id: string; targetType: string; seq: number; deletedAt: string }[];
}

export interface DayNoteItem {
  id: string;
  localDate: string;
  isoYear: number;
  isoWeek: number;
}
export interface StandaloneNoteItem {
  id: string;
  title: string | null;
  occurredAt: string;
  hasTranscript: boolean;
}
export interface StandaloneNoteDetail {
  id: string;
  title: string | null;
  occurredAt: string;
  transcript: string;
  hasAudio: boolean;
  summaries: { id: string; seq: number }[];
}
export type SummaryTargetType = "day" | "week" | "week_partial" | "standalone";
export interface SummaryItem {
  id: string;
  targetType: SummaryTargetType;
  targetDate: string | null;
  isoYear: number | null;
  isoWeek: number | null;
  standaloneNoteId: string | null;
  seq: number;
  isPartial: boolean;
}
/** Réponse des points de génération : identifie le résumé produit, ce qui permet de l'ouvrir aussitôt. */
export interface GeneratedSummary {
  summaryId: string;
  seq: number;
  path: string;
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: Json) => request<T>("POST", path, body),
  patch: <T>(path: string, body?: Json) => request<T>("PATCH", path, body),
  del: <T>(path: string, body?: Json) => request<T>("DELETE", path, body),

  // --- Authentification ---
  me: () => request<Me>("GET", "/api/me"),
  login: (email: string, password: string) =>
    request<{ token: string; user: Me }>("POST", "/api/login", { email, password }),
  logout: () => request<{ ok: true }>("POST", "/api/logout"),

  // --- Réglages, quotas, corbeille ---
  settings: () => request<SettingsResponse>("GET", "/api/settings"),
  updatePreferences: (patch: Partial<Preferences>) => request<{ ok: true }>("PATCH", "/api/settings/preferences", patch),
  updateRetention: (audioRetentionDays: number) =>
    request<{ ok: true }>("PATCH", "/api/settings/retention", { audioRetentionDays }),
  accountReset: (confirm: string) => request<{ ok: true }>("POST", "/api/account/reset", { confirm }),
  usage: () => request<UsageData>("GET", "/api/usage"),
  trash: () => request<TrashData>("GET", "/api/trash"),
  restoreTrash: (type: "capture" | "standalone" | "summary", id: string) =>
    request<{ ok: true }>("POST", "/api/trash/restore", { type, id }),
  purge: () => request<{ purgedAudioSegments: number; purgedTrashItems: number }>("POST", "/api/maintenance/purge"),

  // --- Captures audio (machine à états d'enregistrement) ---
  startCapture: (mode: CaptureMode, standaloneTitle?: string) =>
    request<StartCaptureResult>("POST", "/api/captures/audio/start", {
      mode,
      ...(standaloneTitle ? { standaloneTitle } : {}),
    }),
  uploadSegment: (captureId: string, audio: Blob, durationSeconds: number, fileName: string) => {
    const form = new FormData();
    form.append("durationSeconds", String(durationSeconds));
    form.append("file", audio, fileName);
    return requestForm<{ segmentId?: string; seq?: number }>(`/api/captures/${captureId}/segments`, form);
  },
  deleteLastSegment: (captureId: string) =>
    request<{ deleted: boolean }>("DELETE", `/api/captures/${captureId}/segments/last`),
  finalizeCapture: (captureId: string) =>
    request<{ captureId: string }>("POST", `/api/captures/${captureId}/finalize`),
  deleteCapture: (captureId: string) => request<unknown>("DELETE", `/api/captures/${captureId}`),

  // --- Notes tapées et photos (mode quotidien) ---
  addTextNote: (text: string) => request<{ captureId: string }>("POST", "/api/captures/text", { text }),
  addPhoto: (image: Blob, fileName: string) => {
    const form = new FormData();
    form.append("file", image, fileName);
    return requestForm<{ captureId: string }>("/api/captures/photo", form);
  },

  // --- Mes notes (fenêtre d'édition §9) ---
  listDayNotes: () => request<DayNoteItem[]>("GET", "/api/day-notes"),
  dayNoteContent: (id: string) => request<{ id: string; localDate: string; content: string }>("GET", `/api/day-notes/${id}/content`),
  saveDayNote: (id: string, content: string) => request<{ ok: true }>("PATCH", `/api/day-notes/${id}/content`, { content }),
  deleteDayNote: (id: string) => request<{ ok: true }>("DELETE", `/api/day-notes/${id}`),
  listStandaloneNotes: () => request<StandaloneNoteItem[]>("GET", "/api/standalone-notes"),
  standaloneNote: (id: string) => request<StandaloneNoteDetail>("GET", `/api/standalone-notes/${id}`),
  saveStandaloneTranscript: (id: string, transcript: string) =>
    request<{ ok: true }>("PATCH", `/api/standalone-notes/${id}/transcript`, { transcript }),
  retranscribeStandalone: (id: string) => request<{ transcript: string }>("POST", `/api/standalone-notes/${id}/retranscribe`),
  deleteStandaloneNote: (id: string) => request<{ ok: true }>("DELETE", `/api/standalone-notes/${id}`),

  // --- Comptes rendus (résumés) ---
  listSummaries: () => request<SummaryItem[]>("GET", "/api/summaries"),
  summaryContent: (id: string) => request<{ id: string; content: string }>("GET", `/api/summaries/${id}/content`),
  saveSummary: (id: string, content: string) => request<{ ok: true }>("PATCH", `/api/summaries/${id}/content`, { content }),
  deleteSummary: (id: string) => request<{ ok: true }>("DELETE", `/api/summaries/${id}`),
  generateDaySummary: (date?: string) => request<GeneratedSummary>("POST", "/api/summaries/day", date ? { date } : {}),
  generateWeekSummary: (isoYear?: number, isoWeek?: number, partial?: boolean) =>
    request<GeneratedSummary>("POST", "/api/summaries/week", { isoYear, isoWeek, partial }),
  generateStandaloneSummary: (noteId: string) => request<GeneratedSummary>("POST", `/api/summaries/standalone/${noteId}`),

  // --- Envoi par e-mail d'un élément ---
  sendEmail: (to: string, subject: string, text: string) =>
    request<{ ok: true }>("POST", "/api/email", { to, subject, text }),
};
